// #2355 follow-up: ethers (~300KB) を main bundle から外す。
//
// Web3 (JPYC) mode は **opt-in の実験機能**で、既定は Firebase Auth + Stripe。
// それなのに ethers は全員の初回読み込みに入っていた — `store/useStore.ts` が
// `lib/jpyc` を静的 import していて、そのストアはほぼ全画面が読むため。
//
// #1391 で @web3auth (~1.7MB) を同じ理由で遅延化したのと同じ形にする。違いは、
// あちらが「未ロードの間は null を返す」で済んだのに対し、**こちらは決済の経路を
// 決める値を返す**こと。同期のまま Promise を返すと `!!` が常に true になり、
// **オンチェーンでない支払いまでオンチェーンの経路に送る**。なので:
//
// - `getEthersProvider()` は **async** にして、呼ぶ側に待たせる
//   (呼ぶのは `useStore.pay` と `lib/jpyc` で、どちらも async の中)
// - 画面が「オンチェーンの進捗ラベルを出すか」を同期で知りたいだけのところは
//   `hasWeb3Provider()` を使う。ethers を読まずに答えられる
import type { BrowserProvider, JsonRpcSigner } from "ethers";
import { getWeb3Auth } from "./web3auth";

// Cached provider/signer per session
let cachedProvider: BrowserProvider | null = null;
let cachedSigner: JsonRpcSigner | null = null;

/** ログアウト・アカウント切り替えの世代。`clearEthersCache()` のたびに進む。
 *  **読み込みを待っている間に切り替わったか**を見るために要る (下の説明を参照)。 */
let generation = 0;

/** ethers 本体。**同時に呼ばれても 1 回しか読み込まない。**
 *  窓口を 1 枚挟む理由は `ethersImpl.ts` を参照 (直に import すると chunk が 4 倍)。 */
let loading: Promise<typeof import("./ethersImpl")> | null = null;
export function loadEthers(): Promise<typeof import("./ethersImpl")> {
  if (!loading) {
    loading = import("./ethersImpl").catch((e) => {
      // **失敗は覚えない。** 覚えると、電波が一瞬切れて chunk を取り損ねただけで
      // その後ずっと Web3 の支払いが通らなくなる (画面を読み込み直すまで直らない)。
      // 静的に入れていたときには無かった壊れ方なので、ここで必ず戻す。
      loading = null;
      throw e;
    });
  }
  return loading;
}

/**
 * Web3Auth がつながっているか。**ethers を読み込まずに答える。**
 *
 * 画面が進捗ラベルを出し分けるためだけに使う。`getEthersProvider()` が null を
 * 返す条件と同じ (Web3Auth の provider が無ければ ethers があっても何もできない)。
 */
export function hasWeb3Provider(): boolean {
  return !!getWeb3Auth()?.provider;
}

/**
 * Get ethers.js BrowserProvider from Web3Auth's EIP-1193 provider.
 * Returns null if Web3Auth is not connected.
 */
export async function getEthersProvider(): Promise<BrowserProvider | null> {
  if (cachedProvider) return cachedProvider;
  if (!getWeb3Auth()?.provider) return null;

  const gen = generation;
  const { BrowserProvider } = await loadEthers();

  // **待っている間に変わりうる。** 読み込みが非同期になったので、その間にログアウト
  // (`clearEthersCache`) やアカウントの切り替えが起きる。待つ前に掴んだ provider を
  // そのまま覚えると、終わったセッションのものを抱えたまま次の人に使わせてしまう。
  // **いまの provider を見直してから作る。**
  const w3a = getWeb3Auth();
  if (!w3a?.provider) return null;
  if (gen !== generation || !cachedProvider) {
    cachedProvider = new BrowserProvider(w3a.provider);
  }
  return cachedProvider;
}

/**
 * Get ethers.js Signer from Web3Auth provider.
 * Throws if not connected.
 */
export async function getEthersSigner(): Promise<JsonRpcSigner> {
  if (cachedSigner) return cachedSigner;

  const gen = generation;
  const provider = await getEthersProvider();
  if (!provider) throw new Error("Web3Auth provider not available");

  const signer = await provider.getSigner();
  // 待っている間に切り替わっていたら覚えない (前のセッションの署名者を残さない)
  if (gen === generation) cachedSigner = signer;
  return signer;
}

/** Clear cached provider/signer (call on logout). */
export function clearEthersCache(): void {
  cachedProvider = null;
  cachedSigner = null;
  generation++;
}
